'use client';

import styled, { css, keyframes } from 'styled-components';
import type { ILoaderProps } from '@/components/_promo/components/Loader';
import {
  device,
  doublePadding,
  hexToRGB,
  padding,
  ScreenSize,
  typography,
} from './helpers';

interface ScContainerProps {
  relative?: boolean;
  withMarginBottom?: boolean;
}

interface ScLobbyGamesSectionProps {
  gap?: number;
  withoutMarginBottom?: boolean;
}

export const ScContainer = styled.div<ScContainerProps>`
  max-width: ${ScreenSize.DESKTOP_L + doublePadding}px;
  width: 100%;
  padding: 0 ${padding}px;
  margin: 0 auto;
  margin-bottom: ${({ withMarginBottom }) => (withMarginBottom ? '48px' : '0')};

  .widget {
    height: 300px;
    margin-bottom: 16px;

    @media (${device.mobile}) {
      height: 80vh;
    }
  }

  ${({ relative }) =>
    relative &&
    css`
      position: relative;
    `}

  @media (${device.desktop}) {
    max-width: ${ScreenSize.DESKTOP + doublePadding}px;
  }

  @media (${device.tablet}) {
    max-width: ${ScreenSize.TABLET + doublePadding}px;
  }

  @media (${device.mobile}) {
    max-width: ${ScreenSize.MOBILE + doublePadding}px;
    padding: 0 16px;
  }

  @media (${device.mobileS}) {
    max-width: ${ScreenSize.MOBILE_S + doublePadding}px;
  }
`;

export const ScTextRegular = styled.p`
  color: ${({ theme }) => theme.blackLightColor};
  margin-top: 16px;
  ${typography.body1regular}
`;

export const ScStaticPage = styled.div`
  color: ${({ theme }) => theme.whiteColor};
  @media (${device.mobile}) {
    margin-top: 16px;
  }
  .without-margin-top {
    margin-top: 0 !important;
  }
  h1 {
    text-transform: uppercase;
    margin-bottom: 32px;
    ${typography.headline1}

    @media (${device.mobile}) {
      font-size: 28px;
      line-height: 36px;

      &.h1.de {
        font-size: 21px;
      }
    }
  }
  h2 {
    ${typography.headline2mobile}

    margin-top: 64px;
    margin-bottom: 32px;
    @media (${device.mobile}) {
      font-size: 18px;
      line-height: 26px;
    }
  }
  .static-main h2 {
    text-align: center;
    ${typography.headline2}
    @media (${device.mobile}) {
      ${typography.headline2mobile}
    }
  }
  h2,
  .payment {
    @media (${device.mobile}) {
      text-align: center;
    }
  }
  h4 {
    margin-top: 24px;
  }
  p,
  td,
  th {
    color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
    margin-top: 16px;
    ${typography.body1regular}
  }

  p,
  li {
    & > strong:first-child {
      color: ${({ theme }) => theme.whiteColor};
    }
  }

  th {
    font-weight: 400;
  }

  ul,
  ol {
    ${typography.body1regular}
    color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
    margin: 32px 0 32px 32px;
    & > li {
      &:not(:last-child) {
        margin-bottom: 32px;
      }
    }
  }

  .horizontal-scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }
  th,
  td {
    border: 1px solid ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
    padding: 8px;
    text-align: left;
  }

  p,
  ul,
  ol {
    a {
      color: ${({ theme }) => theme.mainColor};
    }
  }

  ul {
    list-style-type: none;
  }

  .paymentsText {
    max-width: 50%;
    margin: auto;
    margin-bottom: 64px;
    text-align: center;
    @media (${device.mobile}) {
      max-width: 100%;
    }
    h1 {
      margin-bottom: 16px;
    }
    * {
      color: white;
    }
  }
  .categories-container {
    margin-bottom: -32px;
  }
  b {
    color: ${({ theme }) => theme.whiteColor} !important;
  }
  .static-no-margin-top {
    margin-top: -32px;
  }
`;

export const ScStaticPageLastUpdate = styled.div`
  margin: 32px 0 -32px;
  p {
    color: ${({ theme }) => theme.mainColor};
    margin: 0;
    ${typography.body1regular}
  }
`;

export const ScGamesSection = styled.div`
  margin: 0 0 48px 0;
`;

export const ScLobbySliderWrapper = styled.div<ScLobbyGamesSectionProps>`
  display: flex;
  flex-direction: column;
  row-gap: ${({ gap }) => (gap ? `${gap}px` : '48px')};
  width: calc(100vw - (100vw - 100%));
  overflow: hidden;
  margin-bottom: ${({ withoutMarginBottom }) =>
    withoutMarginBottom ? '0' : '48px'};

  @media (${device.tablet}) {
    row-gap: ${({ gap }) => (gap ? `${gap}px` : '32px')};
  }
`;

export const spinning = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

interface SpinnerProps {
  size: number;
  color: string;
  background: string;
  center?: boolean;
  margin?: string;
}

export const Spinner = styled.div<SpinnerProps>`
  font-size: ${({ size }) => `${size}px`};
  margin: ${({ center, margin, size }) => {
    switch (true) {
      case center != null:
        return `-${size / 2}px auto auto -${size / 2}px`;
      case margin != null:
        return margin;
      default:
        return '0px auto';
    }
  }};
  z-index: 10;
  text-indent: -9999em;
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  border-radius: 50%;
  background: ${({ color }) => color};
  background: linear-gradient(
    to right,
    ${({ color }) => color} 10%,
    rgba(255, 255, 255, 0) 42%
  );
  position: ${({ center }) => (center ? 'absolute' : 'relative')};
  top: ${({ center }) => (center ? '50%' : 'auto')};
  left: ${({ center }) => (center ? '50%' : 'auto')};
  animation: ${spinning} 1.4s infinite linear;
  flex-shrink: 0;
  &:before {
    width: 50%;
    height: 50%;
    background: ${({ color }) => color};
    border-radius: 100% 0 0 0;
    position: absolute;
    top: 0;
    left: 0;
    content: '';
  }
  &:after {
    background: ${({ background }) => background};
    width: 75%;
    height: 75%;
    border-radius: 50%;
    content: '';
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
`;

export const GradientSpinner = styled.div<ILoaderProps>`
  width: ${({ size }) => (size ? `${size}px` : '1em')};
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: ${({ borderWidth }) =>
    `${borderWidth ? `${borderWidth}px` : '0.07em'} solid transparent`};
  border-top-color: ${({ color }) => color || 'white'};
  border-left-color: ${({ color }) => color || 'white'};
  background: ${({ background }) => background || 'transparent'};
  animation: ${spinning} 1s linear infinite;
  margin: ${({ margin }) => margin || '0 auto'};
  flex-shrink: 0;
  ${({ center }) =>
    center &&
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      margin: auto;
      z-index: 1;
    `}
`;

export const ScFormBlock = styled.div`
  background: ${({ theme }) => theme.whiteColor};
  color: ${({ theme }) => theme.blackColor};
  border-radius: 16px;
  padding: 36px 36px;
  max-width: 600px;
  margin: 0 auto;
  h1 {
    margin-bottom: 36px;
    text-transform: uppercase;
  }
`;

export const ScRelativeContainer = styled.div`
  position: relative;
`;
